import * as React from 'react';
import { getRelativeBasePath } from 'util/history';
import { getCloudId, getWorkspaceAri } from 'util/meta';
import { styled } from '@compiled/react';
import { useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';
import * as grid from 'view/styles/grid';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import EditorSettingsIcon from '@atlaskit/icon/glyph/editor/settings';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { multiHcDropdownQuery } from './__generated__/multiHcDropdownQuery.graphql';
import messages from './messages';

interface Props {
    currentHcName: string;
}
export const MultiHcDropdown: React.FC<Props> = ({ currentHcName }) => {
    const data = useLazyLoadQuery<multiHcDropdownQuery>(
        graphql`
            query multiHcDropdownQuery($cloudId: ID!, $workspaceAri: ID!) {
                helpCenter(cloudId: $cloudId) {
                    helpCenters(workspaceAri: $workspaceAri) {
                        #TODO: use connections and edges here to support pagination in future
                        ... on HelpCenter {
                            name {
                                default
                            }
                            slug @optIn(to: "HelpCenterSlugTest")
                        }
                        ... on QueryError {
                            __typename
                        }
                    }
                }
            }
        `,
        {
            cloudId: getCloudId(),
            workspaceAri: getWorkspaceAri(),
        }
    );
    const { formatMessage } = useIntl();

    const hasQueryError = data.helpCenter?.helpCenters?.some((hc) => hc?.__typename === 'QueryError');
    if (hasQueryError) {
        return null;
    }

    const helpCenters =
        data.helpCenter?.helpCenters
            ?.map((hc) => {
                if (hc?.slug) {
                    return {
                        hcname: hc?.name?.default || 'Help Center',
                        url: `/helpcenter/${hc?.slug}`,
                    };
                }
                //default helpcenter as slug is null
                return {
                    hcname: hc?.name?.default || 'Help Center',
                    url: '/servicedesk/customer',
                };
            })
            //not showing the already selected HC in dropdown
            .filter((hc) => hc?.url !== getRelativeBasePath())
            //showing only 2 helpcenters in dropdown, this will be changed when this query gets paginated
            .slice(0, 2) || [];

    //TODO: import these urls from the hc-management page
    const helpCenterManagementScreenUrl = '/helpcenter-management';
    const helpCenterManagementScreenWithNewHcCreated = `${helpCenterManagementScreenUrl}?create=true`;
    return (
        <DropdownMenu triggerType="button" trigger={<Box xcss={triggerStyles}>{currentHcName}</Box>}>
            <DropdownItemGroup>
                {helpCenters.map((hc) => (
                    <DropdownItem key={hc.url}>
                        <NoUnderlineLink href={hc.url} key={hc.url}>
                            <Box xcss={menuItemStyles}>{hc.hcname}</Box>
                        </NoUnderlineLink>
                    </DropdownItem>
                ))}
            </DropdownItemGroup>
            <DropdownItemGroup hasSeparator>
                <Box xcss={createManageHelpCenterContainerStyles}>
                    <DropdownItem>
                        <NoUnderlineLink href={helpCenterManagementScreenUrl}>
                            <Box xcss={createManageHelpCenterItemStyles}>
                                <EditorSettingsIcon label="" />
                                {formatMessage(messages.manageHc)}
                            </Box>
                        </NoUnderlineLink>
                    </DropdownItem>

                    <DropdownItem>
                        <NoUnderlineLink href={helpCenterManagementScreenWithNewHcCreated}>
                            <Box xcss={createManageHelpCenterItemStyles}>
                                <EditorAddIcon label="" />
                                {formatMessage(messages.createHc)}
                            </Box>
                        </NoUnderlineLink>
                    </DropdownItem>
                </Box>
            </DropdownItemGroup>
        </DropdownMenu>
    );
};

const NoUnderlineLink = styled.a({
    textDecoration: 'none',
    '&:focus, &:hover, &:visited, &:link, &:active': {
        textDecoration: 'none',
    },
});
const createManageHelpCenterContainerStyles = xcss({ marginLeft: token('space.negative.100', '-8px') });
const createManageHelpCenterItemStyles = xcss({
    color: 'color.text.selected',
    display: 'flex',
    alignItems: 'center',
});
const menuItemStyles = xcss({
    color: 'color.text',
    textDecoration: 'none',
    maxWidth: grid.multiple(28).px,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});
const triggerStyles = xcss({
    maxWidth: grid.multiple(24).px,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});
